import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useEffectivePermissions } from '../auth/useEffectivePermissions'
import { modules } from '../modules/registry'

/**
 * Global command palette (⌘K / Ctrl+K). DESIGN.md §14 — glass sheet
 * centered at the top of the viewport over a blurred backdrop.
 *
 * Content = all modules the user has access to (role-filtered from the
 * registry). Arrow keys navigate, Enter executes, Escape closes.
 *
 * Mount once at the root (already-authenticated shell only).
 */
export default function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [index, setIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { canRead } = useEffectivePermissions()

  // Build the command list from the modules registry, filtered by effective
  // module access (DB-driven, not registry hardcoded roles).
  const commands = useMemo(() => {
    return modules
      .filter(m => canRead(m.id))
      .filter(m => !m.path.includes(':'))   // parameterised routes aren't navigable targets
      .filter(m => !m.noPage)   // rights/widgets without a page (registry flag)
      .map(m => ({ id: m.id, label: m.label ?? m.id, icon: m.icon ?? 'arrow_right_alt', path: m.path }))
  // canRead is referentially stable per render once data loads; we depend on
  // the underlying data via useEffectivePermissions internally.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return commands
    const tokens = q.split(/\s+/).filter(Boolean)
    return commands.filter(c => {
      const hay = `${c.label} ${c.path} ${c.id}`.toLowerCase()
      return tokens.every(t => hay.includes(t))
    })
  }, [commands, query])

  // Global keyboard: open / close / navigate.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Cmd+K or Ctrl+K → toggle
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen(o => !o)
        return
      }
      if (!open) return
      if (e.key === 'Escape') { e.preventDefault(); setOpen(false) }
      if (e.key === 'ArrowDown') { e.preventDefault(); setIndex(i => Math.min(filtered.length - 1, i + 1)) }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setIndex(i => Math.max(0, i - 1)) }
      if (e.key === 'Enter') {
        e.preventDefault()
        const cmd = filtered[index]
        if (cmd) { setOpen(false); setQuery(''); navigate(cmd.path) }
      }
    }
    // External trigger: anyone (e.g. the Header pill) can dispatch
    // window.dispatchEvent(new Event('mc-cmdk-open')) to open the palette.
    const onExternal = () => setOpen(true)
    window.addEventListener('keydown', onKey)
    window.addEventListener('mc-cmdk-open', onExternal)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mc-cmdk-open', onExternal)
    }
  }, [open, filtered, index, navigate])

  // Reset & autofocus on open
  useEffect(() => {
    if (!open) return
    setQuery('')
    setIndex(0)
    const t = setTimeout(() => inputRef.current?.focus(), 10)
    return () => clearTimeout(t)
  }, [open])

  // Keep index inside bounds when filter changes
  useEffect(() => {
    if (index >= filtered.length) setIndex(Math.max(0, filtered.length - 1))
  }, [filtered.length, index])

  // Scroll selected item into view
  useEffect(() => {
    if (!open) return
    const el = listRef.current?.querySelector<HTMLButtonElement>(`[data-cmdp-idx="${index}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [index, open])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-slate-950/40 backdrop-blur-sm animate-[fade-in_0.12s_ease-out] flex items-start justify-center p-6 pt-[20vh]"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false) }}
    >
      <div className="liquid-glass rounded-[24px] w-full max-w-xl flex flex-col overflow-hidden shadow-2xl">
        {/* Search */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/20">
          <span className="material-symbols-outlined text-on-surface-variant">search</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setIndex(0) }}
            placeholder="Seite suchen…"
            className="flex-1 bg-transparent outline-none text-[15px] text-on-surface placeholder:text-on-surface-variant"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="font-mono text-[10px] tracking-[0.12em] uppercase text-on-surface-variant border border-white/25 rounded px-1.5 py-0.5">esc</kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[50vh] overflow-y-auto bg-white/40 dark:bg-slate-900/40">
          {filtered.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-on-surface-variant">
              Keine Treffer für „{query}".
            </div>
          ) : filtered.map((c, i) => (
            <button
              key={c.id}
              data-cmdp-idx={i}
              onMouseEnter={() => setIndex(i)}
              onClick={() => { setOpen(false); setQuery(''); navigate(c.path) }}
              className={`w-full flex items-center gap-3 px-5 py-2.5 text-left transition-colors ${
                i === index
                  ? 'bg-white/70 dark:bg-slate-800/80'
                  : 'hover:bg-white/50 dark:hover:bg-slate-800/60'
              }`}
            >
              <span className="material-symbols-outlined text-primary flex-shrink-0 !text-[20px]">{c.icon}</span>
              <span className="flex-1 min-w-0 flex items-baseline gap-2 truncate">
                <span className="text-[14px] text-on-surface font-medium truncate">{c.label}</span>
                <span className="font-mono text-[10.5px] text-on-surface-variant truncate">{c.path}</span>
              </span>
              {i === index && (
                <kbd className="font-mono text-[10px] tracking-[0.12em] uppercase text-on-surface-variant border border-white/25 rounded px-1.5 py-0.5">↵</kbd>
              )}
            </button>
          ))}
        </div>

        {/* Footer hint */}
        <div className="px-5 py-2.5 border-t border-white/20 flex items-center gap-4 text-[10.5px] text-on-surface-variant font-mono tracking-[0.08em] uppercase">
          <span><kbd className="border border-white/25 rounded px-1 py-px">↑</kbd> <kbd className="border border-white/25 rounded px-1 py-px">↓</kbd> bewegen</span>
          <span><kbd className="border border-white/25 rounded px-1 py-px">↵</kbd> öffnen</span>
          <span className="ml-auto"><kbd className="border border-white/25 rounded px-1 py-px">⌘K</kbd> umschalten</span>
        </div>
      </div>
    </div>,
    document.body,
  )
}
